import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './MapViewer.css';

const MapViewer = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [svgContent, setSvgContent] = useState(null);
  const [plots, setPlots] = useState([]);
  const [hoveredPlot, setHoveredPlot] = useState(null);
  const [selectedPlot, setSelectedPlot] = useState(null);
  const [projectName, setProjectName] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [suggestions, setSuggestions] = useState([]);
  const [showSuccessMsg, setShowSuccessMsg] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingData, setBookingData] = useState({ name: '', contact: '', email: '' });
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);       // project doesn't exist
  const [notReady, setNotReady] = useState(false);       // project exists but has no map yet
  const [svgReady, setSvgReady] = useState(false);


  const mapRef = useRef(null);
  const overlayRef = useRef(null);
  const listenersRef = useRef([]);
  const currentGlowRef = useRef(null);
  const searchRef = useRef(null);



  const plotCounts = useMemo(() => {
    const counts = { available: 0, sold: 0, booked: 0, total: plots.length };
    plots.forEach(plot => {
      const status = plot.status?.toLowerCase() || 'available';
      if (counts.hasOwnProperty(status)) counts[status]++;
    });
    return counts;
  }, [plots]);


  // Re-fetch plots every 30 seconds
  useEffect(() => {
    if (!projectId) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/plots/${projectId}`);
        setPlots(res.data.plots || []);
      } catch (err) {
        console.error('Poll error:', err);
      }
    }, 30000); // every 30s

    return () => clearInterval(interval);
  }, [projectId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectRes, plotsRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/projects/${projectId}`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/plots/${projectId}`)
        ]);
        const project = projectRes.data.project;

        //Project row exists but no SVG uploaded yet
        if (!project) {
          setNotFound(true);
          return;
        }

        setProjectName(project.name);
        setPlots(plotsRes.data.plots || []);

        const svgFileName = project.svg_filename;

        if (!svgFileName) {
          setNotReady(true);
          return;
        };

        try {
          const svgRes = await axios.get(`${process.env.REACT_APP_API_URL}/uploads/${svgFileName}`);
          setSvgContent(svgRes.data);
        } catch {
          setNotReady(true);
        }


      } catch (error) {
        if (error.response?.status === 404) {
          setNotFound(true);
        } else {
          // Any other error (ntwork, 500) - trat as not ready
          setNotReady(true);
        }
        console.error('Error loading map data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [projectId]);

  const injectPlotLabels = () => {
    const svgRoot = document.querySelector('.leaflet-overlay-pane svg');
    if (!svgRoot) return;
    const polygons = svgRoot.querySelectorAll("polygon[id*='no-']");
    polygons.forEach(poly => {
      const plotNum = poly.id.replace('no-', '').trim();
      const points = poly.getAttribute('points')?.trim().split(' ').map(pt => pt.split(',').map(Number));
      if (!points || points.length < 3) return;
      const centroid = points.reduce((acc, [x, y]) => ({ x: acc.x + x / points.length, y: acc.y + y / points.length }), { x: 0, y: 0 });
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.textContent = plotNum;
      text.setAttribute('x', centroid.x);
      text.setAttribute('y', centroid.y);
      text.setAttribute('class', 'plot-label');
      svgRoot.appendChild(text);
    });
  };

  const waitForSVG = () => {
    const overlayPane = document.querySelector('.leaflet-overlay-pane');
    if (!overlayPane) return;
    const observer = new MutationObserver(() => {
      const svgRoot = document.querySelector('.leaflet-overlay-pane svg');
      if (svgRoot) {
        observer.disconnect();
        injectPlotLabels();
        setSvgReady(true);
      }
    });
    observer.observe(overlayPane, { childList: true, subtree: true });
  };

  useEffect(() => {
    if (!svgContent) return;
    if (mapRef.current) return;

    const map = L.map('map', {
      zoomControl: false,
      attributionControl: false,
      minZoom: 2.1,
      maxZoom: 5,
    });

    L.control.zoom({ position: 'bottomright' }).addTo(map);
    mapRef.current = map;

    map.on('zoomend', () => {
      const zoom = map.getZoom();
      const scale = zoom * 2.5;
      document.querySelectorAll('#map svg text').forEach(txt => {
        txt.style.fontSize = `${10 + scale}px`;
      });
    });

    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
    const svgEl = svgDoc.documentElement;
    const viewBox = svgEl.getAttribute('viewBox').split(' ');
    const bounds = [[0, 0], [viewBox[3], viewBox[2]]];

    overlayRef.current = L.svgOverlay(svgEl, bounds, {
      interactive: true,
      className: 'leaflet-svg-overlay'
    }).addTo(map);

    map.fitBounds(bounds, { padding: [20, 20] });
    waitForSVG();

    return () => {
      map.remove();
      mapRef.current = null;
      setSvgReady(false);
    };
  }, [svgContent]);

  const cleanupListeners = () => {
    try {
      (listenersRef.current || []).forEach(({ el, type, handler }) => {
        if (el && handler) el.removeEventListener(type, handler);
      });
    } catch (err) { }
    finally {
      listenersRef.current = [];
    }
  };

  const clearCurrentGlow = () => {
    const el = currentGlowRef.current;
    if (el) {
      el.classList.remove('plot-glow');
      currentGlowRef.current = null;
    }
  };

  useEffect(() => {
    if (!svgContent || plots.length === 0) return;
    const svgRoot = document.querySelector('#map svg');
    if (!svgRoot) return;

    plots.forEach(plot => {
      const el = svgRoot.querySelector(`#${plot.svg_id}`);
      if (!el) return;
      const status = plot.status?.toLowerCase() || 'available';
      const colors = { available: '#22c55e', booked: '#f59e0b', sold: '#ef4444' };
      el.style.fill = colors[status] || colors.available;
      el.style.cursor = 'pointer';
      el.style.transition = 'stroke-width 0.2s ease, filter 0.2s ease';
      el.style.pointerEvents = 'auto';

      const mouseOverHandler = (e) => {
        setHoveredPlot(plot);
        setTooltipPosition({ x: e.clientX + 14, y: e.clientY + 14 });
        el.style.strokeWidth = '2.5px';
        el.style.stroke = '#000';
      };
      const mouseOutHandler = () => {
        setHoveredPlot(null);
        el.style.strokeWidth = '1px';
        el.style.stroke = '';
      };
      const clickHandler = () => {
        clearCurrentGlow();
        setSelectedPlot(plot);
        const target = document.querySelector(`#${plot.svg_id}`);
        if (target) {
          target.classList.add('plot-glow');
          currentGlowRef.current = target;
        }
      };

      el.addEventListener('mouseover', mouseOverHandler);
      el.addEventListener('mouseout', mouseOutHandler);
      el.addEventListener('click', clickHandler);

      listenersRef.current.push(
        { el, type: 'mouseover', handler: mouseOverHandler },
        { el, type: 'mouseout', handler: mouseOutHandler },
        { el, type: 'click', handler: clickHandler }
      );
    });

    return () => {
      cleanupListeners();
    };
  }, [svgContent, plots]);

  useEffect(() => {
    if (!selectedPlot) clearCurrentGlow();
  }, [selectedPlot]);

  const handleSearchChange = (value) => {
    setSearchText(value);
    clearCurrentGlow();
    if (!value.trim()) {
      setSuggestions([]);
      return;
    }
    const matches = plots.filter(p => p.plot_number.toString().toLowerCase().includes(value.trim().toLowerCase())).slice(0, 6);
    setSuggestions(matches);
  };

  const handleSuggestionClick = (plot) => {
    setSelectedPlot(plot);
    setSearchText(plot.plot_number.toString());
    setSuggestions([]);
    setSearchOpen(false);
    clearCurrentGlow();
    const svgRoot = document.querySelector('#map svg');
    const el = svgRoot?.querySelector(`#${plot.svg_id}`);
    if (el) {
      el.classList.add('plot-glow');
      currentGlowRef.current = el;
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/bookings`, {
        project_id: projectId,
        plot_id: selectedPlot.id,
        project_name: projectName,
        plot_no: selectedPlot.plot_number,
        customer_name: bookingData.name,
        customer_email: bookingData.email,
        customer_phone: bookingData.contact,
      });
      setBookingData({ name: '', contact: '', email: '' });
      if (res.status === 200) {
        setShowBookingModal(false);
        setSelectedPlot(null);
        setShowSuccessMsg(true);
        setTimeout(() => setShowSuccessMsg(false), 5000);
      }
    } catch (error) {
      console.error('Booking error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const capitalize = str => str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

  const formatPrice = price =>
    new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', maximumFractionDigits: 0 }).format(price);

  const statusBadgeClass = (status) => {
    const s = status?.toLowerCase();
    if (s === 'available') return 'badge badge--available';
    if (s === 'booked') return 'badge badge--booked';
    if (s === 'sold') return 'badge badge--sold';
    return 'badge';
  };

  if (loading) {
    return (
      <div className="mv-loading">
        <div className="mv-loading__spinner"></div>
        <p>Loading project map…</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="mv-empty">
        <div className="mv-empty__icon">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="24" r="23" stroke="#e5e7eb" strokeWidth="1.5" />
            <path d="M16 24h16M24 16v16" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </div>
        <h2 className="mv-empty__title">Project not found</h2>
        <p className="mv-empty__desc">This project doesn't exist or may have been removed.</p>
        <button className="mv-btn mv-btn--ghost mv-empty__btn" onClick={() => navigate(-1)}>
          ← Go back
        </button>
      </div>
    );
  }

  if (notReady) {
    return (
      <div className="mv-empty">
        <img
          src="https://mawega.co.ke/wp-content/uploads/2024/06/logo-1024x1024.png"
          height={40}
          alt="Mawega"
          className="mv-empty__logo"
        />
        <div className="mv-empty__icon mv-empty__icon--pulse">
          <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
            <circle cx="26" cy="26" r="25" stroke="#e5e7eb" strokeWidth="1.5" />
            <path d="M18 34l8-8 4 4 8-10" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="26" cy="20" r="3" fill="#d1d5db" />
          </svg>
        </div>
        <h2 className="mv-empty__title">
          {projectName ? `${projectName} is being prepared` : 'Map coming soon'}
        </h2>
        <p className="mv-empty__desc">
          Our team is currently setting up the plot map for this project.<br />
          Check back soon or contact us for more details.
        </p>
        <div className="mv-empty__actions">
          <button className="mv-btn mv-btn--primary mv-empty__btn" onClick={() => navigate(-1)}>
            ← Back to projects
          </button>
          <a
            href="https://mawega.co.ke"
            className="mv-btn mv-btn--ghost mv-empty__btn"
            target="_blank"
            rel="noreferrer"
          >
            Visit Mawega
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="mv-root">
      {/* Header */}
      <header className="mv-header">
        <div className="mv-header__left">
          <button className="mv-back-btn" onClick={() => navigate(-1)} aria-label="Back">
            <Link className="mv-back-link">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back
            </Link>
          </button>
          <div className="mv-header__divider" />
          <h1 className="mv-header__title">{projectName || 'Loading…'}</h1>
        </div>
        <div className="mv-header__right">
          <img src="https://mawega.co.ke/wp-content/uploads/2024/06/logo-1024x1024.png" height={36} alt="Mawega" className="mv-logo" />
        </div>
      </header>

      {/* Map area */}
      <div className="mv-map-wrapper">
        <div id="map" className="mv-map" />

        {/* Legend */}
        <div className="mv-legend">
          <div className="mv-legend__item">
            <span className="mv-legend__dot mv-legend__dot--available" />
            <span>Available <strong>{plotCounts.available}</strong></span>
          </div>
          <div className="mv-legend__item">
            <span className="mv-legend__dot mv-legend__dot--booked" />
            <span>Booked <strong>{plotCounts.booked}</strong></span>
          </div>
          <div className="mv-legend__item">
            <span className="mv-legend__dot mv-legend__dot--sold" />
            <span>Sold <strong>{plotCounts.sold}</strong></span>
          </div>
        </div>

        {/* Search */}
        <div className="mv-search" ref={searchRef}>
          {!searchOpen ? (
            <button className="mv-search__toggle" onClick={() => setSearchOpen(true)} aria-label="Search plots">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
                <path d="M10.5 10.5L13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          ) : (
            <div className="mv-search__field">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="mv-search__icon">
                <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
                <path d="M10.5 10.5L13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <input
                autoFocus
                type="text"
                placeholder="Search plot number…"
                value={searchText}
                onChange={e => handleSearchChange(e.target.value)}
                className="mv-search__input"
              />
              <button className="mv-search__clear" onClick={() => { setSearchOpen(false); setSearchText(''); setSuggestions([]); clearCurrentGlow(); }}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
              {suggestions.length > 0 && (
                <ul className="mv-search__suggestions">
                  {suggestions.map(s => (
                    <li key={s.id} onClick={() => handleSuggestionClick(s)} className="mv-search__suggestion-item">
                      <span className="mv-search__suggestion-num">{s.plot_number}</span>
                      <span className={statusBadgeClass(s.status)}>{capitalize(s.status)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Hover Tooltip */}
      {hoveredPlot && (
        <div className="mv-tooltip" style={{ top: tooltipPosition.y, left: tooltipPosition.x }}>
          <span className="mv-tooltip__name">{hoveredPlot.plot_number}</span>
          <span className={statusBadgeClass(hoveredPlot.status)}>{capitalize(hoveredPlot.status)}</span>
        </div>
      )}

      {/* Plot Detail Sidebar */}
      {selectedPlot && (
        <div className="mv-sidebar">
          <div className="mv-sidebar__header">
            <div>
              <p className="mv-sidebar__label">Plot details</p>
              <h2 className="mv-sidebar__title">{selectedPlot.plot_number}</h2>
            </div>
            <button className="mv-sidebar__close" onClick={() => setSelectedPlot(null)} aria-label="Close">
              <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
                <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <div className="mv-sidebar__body">
            <div className="mv-detail-row">
              <span className="mv-detail-label">Status</span>
              <span className={statusBadgeClass(selectedPlot.status)}>{capitalize(selectedPlot.status)}</span>
            </div>
            <div className="mv-detail-row">
              <span className="mv-detail-label">Size</span>
              <span className="mv-detail-value">{selectedPlot.size || '—'}</span>
            </div>
            <div className="mv-detail-row">
              <span className="mv-detail-label">Price</span>
              <span className="mv-detail-value mv-detail-value--price">{formatPrice(selectedPlot.price)}</span>
            </div>
          </div>

          <div className="mv-sidebar__footer">
            <button
              className="mv-btn mv-btn--primary"
              onClick={() => setShowBookingModal(true)}
              disabled={selectedPlot.status?.toLowerCase() !== 'available'}
            >
              {selectedPlot.status?.toLowerCase() === 'available' ? 'Book this plot' : capitalize(selectedPlot.status)}
            </button>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {showBookingModal && selectedPlot && (
        <div className="mv-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowBookingModal(false); }}>
          <div className="mv-modal">
            <div className="mv-modal__header">
              <div>
                <p className="mv-modal__subtitle">Booking request</p>
                <h2 className="mv-modal__title">{selectedPlot.plot_number}</h2>
              </div>
              <button className="mv-sidebar__close" onClick={() => setShowBookingModal(false)}>
                <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
                  <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <form className="mv-modal__form" onSubmit={handleBooking}>
              <div className="mv-field">
                <label className="mv-field__label">Full name</label>
                <input
                  className="mv-field__input"
                  type="text"
                  required
                  placeholder="Enter your full name"
                  value={bookingData.name}
                  onChange={e => setBookingData({ ...bookingData, name: e.target.value })}
                />
              </div>
              <div className="mv-field">
                <label className="mv-field__label">Phone number</label>
                <input
                  className="mv-field__input"
                  type="tel"
                  required
                  placeholder="07XXXXXXXX"
                  value={bookingData.contact}
                  onChange={e => setBookingData({ ...bookingData, contact: e.target.value })}
                />
              </div>
              <div className="mv-field">
                <label className="mv-field__label">Email address</label>
                <input
                  className="mv-field__input"
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={bookingData.email}
                  onChange={e => setBookingData({ ...bookingData, email: e.target.value })}
                />
              </div>

              <div className="mv-modal__actions">
                <button type="button" className="mv-btn mv-btn--ghost" onClick={() => setShowBookingModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="mv-btn mv-btn--primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting…' : 'Confirm booking'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Success Toast */}
      {showSuccessMsg && (
        <div className="mv-toast">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="7" fill="#22c55e" />
            <path d="M5 8l2 2 4-4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Booking submitted! We'll be in touch soon.
        </div>
      )}
    </div>
  );
};

export default MapViewer;